import React, { useState, useEffect } from 'react';
import '../../styles/invoices.css';
import { Link } from 'react-router-dom';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
  FilterList as FilterListIcon,
  Print as PrintIcon,
  LocalShipping as EwayBillIcon,
  Share as ShareIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import InvoicePreviewModal from '../../components/Modals/InvoicePreviewModal';
import ConfirmModal from '../../components/Modals/ConfirmModal';
import { authFetch } from '../../utils/api';
import { invoiceService } from '../../utils/invoiceService';
import { inventoryAPI } from '../../utils/inventoryApi';

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const str = String(dateString).trim();
    const cleanDate = str.includes('T') ? str.split('T')[0] : str;
    const parts = cleanDate.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return str;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (e) {
    return String(dateString);
  }
};

const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    dateRange: {
      from: '',
      to: '',
    },
    status: 'All',
    amountRange: {
      min: '',
      max: '',
    },
  });

  const [showFilters, setShowFilters] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [downloadingInvoices, setDownloadingInvoices] = useState([]);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState('preview');
  const [confirmDelete, setConfirmDelete] = useState({ open: false, invoiceIds: [] });
  const [isFixing, setIsFixing] = useState(false);
  const [printFormat, setPrintFormat] = useState('standard'); // 'standard' or 'thermal'
  const [openDropdowns, setOpenDropdowns] = useState(new Set());
  const [printingInvoices, setPrintingInvoices] = useState(new Set());
  const [printOptions, setPrintOptions] = useState({ open: false, invoice: null });

  useEffect(() => {
    // Fetch invoices from backend
    const fetchInvoices = async () => {
      try {
        const res = await authFetch('/invoices');
        if (res.ok) {
          const data = await res.json();
          setInvoices(data);
        } else {
          setInvoices([]);
        }
      } catch (err) {
        setInvoices([]);
      }
    };
    fetchInvoices();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.print-dropdown')) {
        setOpenDropdowns(new Set());
        // Close all dropdowns
        document.querySelectorAll('.print-dropdown-menu').forEach(dropdown => {
          dropdown.style.display = 'none';
          dropdown.style.visibility = 'hidden';
        });
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Removed unused handleViewInvoice function

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDateRangeChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [field]: value,
      },
    }));
  };

  const handleAmountRangeChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      amountRange: {
        ...prev.amountRange,
        [field]: value,
      },
    }));
  };

  const handleSelectInvoice = (invoiceId) => {
    setSelectedInvoices(prev => {
      if (prev.includes(invoiceId)) {
        return prev.filter(id => id !== invoiceId);
      }
      return [...prev, invoiceId];
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedInvoices(filteredInvoices.map(invoice => invoice.invoice_id));
    } else {
      setSelectedInvoices([]);
    }
  };

  const handleDeleteInvoices = (invoiceIds = selectedInvoices) => {
    setConfirmDelete({ open: true, invoiceIds });
  };

  const confirmDeleteInvoices = async () => {
    const invoiceIds = confirmDelete.invoiceIds;
    setConfirmDelete({ open: false, invoiceIds: [] });

    const successfulDeletes = [];
    const failedDeletes = [];

    for (const id of invoiceIds) {
      try {
        const response = await authFetch(`/invoices/${id}`, { method: 'DELETE' });
        if (response.ok) {
          successfulDeletes.push(id);
        } else {
          console.error(`Failed to delete invoice ${id}:`, response.statusText);
          failedDeletes.push(id);
        }
      } catch (error) {
        console.error(`Error deleting invoice ${id}:`, error);
        failedDeletes.push(id);
      }
    }

    if (successfulDeletes.length > 0) {
      setInvoices(prev => prev.filter(inv => !successfulDeletes.includes(inv.invoice_id)));
    }

    if (failedDeletes.length > 0) {
      alert(`Failed to delete ${failedDeletes.length} invoice(s). Please check console for details.`);
    }

    setSelectedInvoices([]);
  };

  const handleCancelInvoice = async (invoice) => {
    if (!window.confirm(`Are you sure you want to cancel this ${invoice.document_type === 'delivery_challan' ? 'delivery challan' : invoice.document_type === 'credit_note' ? 'credit note' : invoice.document_type === 'debit_note' ? 'debit note' : 'invoice'}? This will delete the document and restore any stock to inventory.`)) {
      return;
    }

    try {
      // 1. Restore Inventory
      if (invoice.items && Array.isArray(invoice.items)) {
        await inventoryAPI.restoreStockForInvoice(invoice.items, invoice.invoice_id, 'Document cancelled via InvoicesPage');
      }

      // 2. Delete Invoice from Database
      const response = await authFetch(`/invoices/${invoice.invoice_id}`, { method: 'DELETE' });
      if (response.ok) {
        setInvoices(prev => prev.filter(inv => inv.invoice_id !== invoice.invoice_id));
        alert('Document cancelled successfully and stock restored.');
      } else {
        console.error(`Failed to cancel document ${invoice.invoice_id}:`, response.statusText);
        alert('Failed to delete the document from the database.');
      }
    } catch (error) {
      console.error(`Error cancelling document ${invoice.invoice_id}:`, error);
      alert('An error occurred while cancelling the document.');
    }
  };

  const handleDuplicateInvoice = (invoice) => {
    const newInvoice = {
      ...invoice,
      invoiceDetails: {
        ...invoice.invoiceDetails,
        number: generateInvoiceNumber(),
        date: new Date().toISOString().split('T')[0]
      }
    };

    const updatedInvoices = [...invoices, newInvoice];
    setInvoices(updatedInvoices);
  };

  const generateInvoiceNumber = () => {
    const prefix = 'TS/SL/24-25';
    const lastNumber = invoices.length > 0
      ? parseInt(invoices[invoices.length - 1].invoice_number.split('/').pop())
      : 190;
    return `${prefix}/${lastNumber + 1}`;
  };

  const handleDownloadInvoices = async (selectedIds = selectedInvoices) => {
    try {
      setDownloading(true);
      setDownloadingInvoices(selectedIds);

      // If multiple invoices are selected, download them one by one
      for (const invoiceId of selectedIds) {
        await invoiceService.downloadInvoicePDF(invoiceId);
        // Add a small delay between downloads to prevent browser issues
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    } catch (error) {
      console.error('Error downloading invoices:', error);
      alert('Failed to download invoice(s). Please try again.');
    } finally {
      setDownloading(false);
      setDownloadingInvoices([]);
    }
  };

  const handleDownloadSingleInvoice = async (invoice) => {
    try {
      const invoiceId = invoice.invoice_id;
      setDownloadingInvoices(prev => [...prev, invoiceId]);
      await invoiceService.downloadInvoicePDF(invoiceId);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Failed to download invoice. Please try again.');
    } finally {
      const invoiceId = invoice.invoice_id;
      setDownloadingInvoices(prev => prev.filter(id => id !== invoiceId));
    }
  };

  const handlePreviewInvoice = async (invoice) => {
    setPreviewMode('preview');
    setViewingInvoice(invoice);
    setIsViewModalOpen(true);
  };

  const handleShareInvoice = async (invoice) => {
    setPreviewMode('share');
    setViewingInvoice(invoice);
    setIsViewModalOpen(true);
  };

  const handlePrintInvoice = async (invoice, format = 'standard') => {
    try {
      // Guard: prevent multiple prints for the same invoice
      if (printingInvoices.has(invoice.invoice_id)) {
        return;
      }
      setPrintingInvoices(prev => new Set(prev).add(invoice.invoice_id));

      const invoiceId = invoice.invoice_id;
      let endpoint = `/invoices/${invoiceId}/pdf/preview`;

      // Add format parameter for thermal printing
      if (format === 'thermal') {
        endpoint += '?format=thermal';
      }

      console.log('Printing invoice:', invoiceId, 'Format:', format);

      const response = await authFetch(endpoint);

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        console.log('PDF generated successfully, opening print window');

        // Create a new window for printing
        const printWindow = window.open(url, '_blank', 'width=800,height=600');

        if (printWindow) {
          // Wait for the window to load completely
          printWindow.addEventListener('load', function () {
            console.log('Print window loaded, starting print');
            setTimeout(() => {
              printWindow.focus();
              printWindow.print();
            }, 1000); // Wait 1 second for content to render
          });

          // Clean up after printing
          printWindow.addEventListener('afterprint', function () {
            console.log('Print completed, closing window after short delay');
            setTimeout(() => {
              try { printWindow.close(); } catch { }
              URL.revokeObjectURL(url);
              setPrintingInvoices(prev => {
                const next = new Set(prev);
                next.delete(invoiceId);
                return next;
              });
            }, 5000); // keep the window open a bit longer before closing
          });

          // Fallback cleanup after 60 seconds (extended)
          setTimeout(() => {
            if (!printWindow.closed) {
              console.log('Fallback cleanup triggered');
              printWindow.close();
              URL.revokeObjectURL(url);
              setPrintingInvoices(prev => {
                const next = new Set(prev);
                next.delete(invoiceId);
                return next;
              });
            }
          }, 60000);
        } else {
          console.log('Popup blocked, using iframe fallback');
          // Fallback to iframe if popup is blocked
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.style.width = '100%';
          iframe.style.height = '600px';
          iframe.src = url;
          document.body.appendChild(iframe);

          iframe.addEventListener('load', function () {
            console.log('Iframe loaded, starting print');
            setTimeout(() => {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();
              // Clean up after printing
              setTimeout(() => {
                document.body.removeChild(iframe);
                URL.revokeObjectURL(url);
                setPrintingInvoices(prev => {
                  const next = new Set(prev);
                  next.delete(invoiceId);
                  return next;
                });
              }, 2000);
            }, 1000);
          });
        }
      } else {
        // Try to get error details from response
        let errorMessage = `Failed to generate PDF: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error printing invoice:', error);

      // Provide more specific error messages
      let userMessage = `Failed to print invoice: ${error.message}`;
      if (error.message.includes('Browser session ended unexpectedly')) {
        userMessage = 'Print failed due to system resources. Please try again in a moment.';
      } else if (error.message.includes('timeout')) {
        userMessage = 'Print generation timed out. Please try again.';
      } else if (error.message.includes('Browser dependencies')) {
        userMessage = 'Print service is temporarily unavailable. Please contact support.';
      } else if (error.message.includes('empty')) {
        userMessage = 'Generated PDF is empty. Please try again.';
      }

      alert(userMessage);
      setPrintingInvoices(prev => {
        const next = new Set(prev);
        next.delete(invoice.invoice_id);
        return next;
      });
    }
  };

  const handleThermalPrint = async (invoice) => {
    await handlePrintInvoice(invoice, 'thermal');
  };

  const handleGenerateEwayBill = async (invoice) => {
    // Direct redirect to official GST E-way bill portal
    window.open('https://ewaybillgst.gov.in/', '_blank');
  };

  const handleBulkEwayBillGeneration = async () => {
    // Direct redirect to official GST E-way bill portal
    window.open('https://ewaybillgst.gov.in/', '_blank');
  };

  const handleFixCalculations = async () => {
    if (isFixing) return;

    if (!window.confirm('This will recalculate totals for invoices that show 0.00. Continue?')) {
      return;
    }

    setIsFixing(true);
    try {
      const response = await authFetch('/invoices/fix/recalculate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Successfully fixed ${result.totalFixed} out of ${result.totalProcessed} invoices.`);

        // Refresh the invoices list
        const res = await authFetch('/invoices');
        if (res.ok) {
          const data = await res.json();
          setInvoices(data);
        }
      } else {
        throw new Error('Failed to fix calculations');
      }
    } catch (error) {
      console.error('Error fixing calculations:', error);
      alert('Failed to fix calculations. Please try again.');
    } finally {
      setIsFixing(false);
    }
  };

  const handleCleanDuplicates = async () => {
    if (!window.confirm('This will remove duplicate invoices with the same amount and client. Continue?')) {
      return;
    }

    // Group invoices by amount and client
    const groups = {};
    invoices.forEach(invoice => {
      const key = `${invoice.total}-${invoice.bill_to_name}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(invoice);
    });

    // Find duplicates (groups with more than 1 invoice)
    const duplicatesToDelete = [];
    Object.values(groups).forEach(group => {
      if (group.length > 1) {
        // Keep the first one, delete the rest
        duplicatesToDelete.push(...group.slice(1));
      }
    });

    if (duplicatesToDelete.length === 0) {
      alert('No duplicate invoices found!');
      return;
    }

    if (!window.confirm(`Found ${duplicatesToDelete.length} duplicate invoices. Delete them?`)) {
      return;
    }

    try {
      // Delete duplicates one by one
      for (const invoice of duplicatesToDelete) {
        await authFetch(`/invoices/${invoice.invoice_id}`, {
          method: 'DELETE',
        });
      }

      alert(`Successfully deleted ${duplicatesToDelete.length} duplicate invoices!`);

      // Refresh the invoices list
      const res = await authFetch('/invoices');
      if (res.ok) {
        const data = await res.json();
        setInvoices(data);
      }
    } catch (error) {
      console.error('Error cleaning duplicates:', error);
      alert('Failed to clean duplicates. Please try again.');
    }
  };

  // Enhanced search functionality
  const filteredInvoices = invoices.filter(invoice => {
    // Defensive: ensure fields exist and are strings/numbers
    const billToName = (invoice.bill_to_name || '').toString().toLowerCase();
    const billToMobile = (invoice.bill_to_mobile || '').toString().toLowerCase();
    const invoiceNumber = (invoice.invoice_number || '').toString().toLowerCase();
    const search = (filters.search || '').toLowerCase().trim();

    // Enhanced search filter - search in multiple fields
    let searchMatch = true;
    if (search) {
      // Split search terms to allow multiple keyword search
      const searchTerms = search.split(/\s+/).filter(term => term.length > 0);

      searchMatch = searchTerms.every(term =>
        billToName.includes(term) ||
        billToMobile.includes(term) ||
        invoiceNumber.includes(term) ||
        (invoice.bill_to_address || '').toLowerCase().includes(term) ||
        (invoice.company_name || '').toLowerCase().includes(term)
      );
    }

    // Date range filter
    let invoiceDate = null;
    try {
      invoiceDate = invoice.invoice_date ? new Date(invoice.invoice_date) : null;
    } catch {
      invoiceDate = null;
    }
    const fromDate = filters.dateRange.from ? new Date(filters.dateRange.from) : null;
    const toDate = filters.dateRange.to ? new Date(filters.dateRange.to) : null;
    const dateFromMatch = !fromDate || (invoiceDate && invoiceDate >= fromDate);
    const dateToMatch = !toDate || (invoiceDate && invoiceDate <= toDate);

    // Amount range filter
    const amount = Number(invoice.total) || 0;
    const minAmount = filters.amountRange.min ? parseFloat(filters.amountRange.min) : null;
    const maxAmount = filters.amountRange.max ? parseFloat(filters.amountRange.max) : null;
    const amountMinMatch = minAmount === null || amount >= minAmount;
    const amountMaxMatch = maxAmount === null || amount <= maxAmount;

    return searchMatch && dateFromMatch && dateToMatch && amountMinMatch && amountMaxMatch;
  });

  // Sort newest invoices first (by date, fallback to invoice_number if needed)
  const sortedFilteredInvoices = [...filteredInvoices].sort((a, b) => {
    const dateA = a.invoice_date ? new Date(a.invoice_date) : new Date(0);
    const dateB = b.invoice_date ? new Date(b.invoice_date) : new Date(0);
    if (dateB - dateA !== 0) return dateB - dateA;
    // Fallback: sort by invoice_number descending if dates are equal or missing
    return (b.invoice_number || '').localeCompare(a.invoice_number || '');
  });

  return (
    <main className="page-container">
      {/* Header */}
      <header className="page-header">
        <div>
          <h1 className="page-title">Invoices</h1>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            className="btn btn-info"
            onClick={handleBulkEwayBillGeneration}
            title="Open GST E-way Bill Portal for qualifying invoices (over ₹50,000)"
            style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
          >
            <EwayBillIcon fontSize="small" />
            E-way Bill Portal
          </button>
          <button
            className={`btn btn-secondary ${isFixing ? 'disabled' : ''}`}
            onClick={handleFixCalculations}
            disabled={isFixing}
            title="Fix calculation issues for existing invoices"
          >
            {isFixing ? 'Fixing...' : 'Fix Calculations'}
          </button>
          <button
            className="btn btn-warning"
            onClick={handleCleanDuplicates}
            title="Remove duplicate invoices with same amounts"
          >
            Clean Duplicates
          </button>
          <Link to="/invoices/create" className="btn btn-primary">
            <AddIcon fontSize="small" />
            Create Invoice
          </Link>
        </div>
      </header>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <SearchIcon fontSize="small" className="search-icon" />
          <input
            type="text"
            placeholder="Search by invoice number, customer name, or mobile number..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="search-input"
          />
          {filters.search && (
            <button
              className="clear-search"
              onClick={() => handleFilterChange('search', '')}
              title="Clear search"
            >
              ×
            </button>
          )}
        </div>

        <button
          className={`btn btn-secondary filter-toggle ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <FilterListIcon fontSize="small" />
          Filters
        </button>

        {showFilters && (
          <div className="advanced-filters">
            <div className="filter-group">
              <label>Date Range</label>
              <div className="date-range">
                <input
                  type="date"
                  value={filters.dateRange.from}
                  onChange={(e) => handleDateRangeChange('from', e.target.value)}
                  className="date-input"
                />
                <span>to</span>
                <input
                  type="date"
                  value={filters.dateRange.to}
                  onChange={(e) => handleDateRangeChange('to', e.target.value)}
                  className="date-input"
                />
              </div>
            </div>

            <div className="filter-group">
              <label>Amount Range</label>
              <div className="amount-range">
                <div className="amount-input">
                  <span className="currency-symbol">₹</span>
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.amountRange.min}
                    onChange={(e) => handleAmountRangeChange('min', e.target.value)}
                    className="form-input"
                  />
                </div>
                <span>to</span>
                <div className="amount-input">
                  <span className="currency-symbol">₹</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.amountRange.max}
                    onChange={(e) => handleAmountRangeChange('max', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            <button
              className="btn btn-secondary"
              onClick={() => setFilters({
                search: '',
                dateRange: { from: '', to: '' },
                status: 'All',
                amountRange: { min: '', max: '' },
              })}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Search Results Info */}
      {filters.search && (
        <div className="search-results-info">
          <span className="search-results-text">
            {sortedFilteredInvoices.length} of {invoices.length} invoices found for "{filters.search}"
          </span>
        </div>
      )}

      {/* Actions Bar */}
      <div className="actions-bar">
        {selectedInvoices.length > 0 && (
          <div className="bulk-actions">
            <button className="btn btn-danger" onClick={() => handleDeleteInvoices()}>
              <DeleteIcon fontSize="small" />
              Delete Selected
            </button>
            <button
              className={`btn btn-secondary ${downloading ? 'disabled' : ''}`}
              onClick={() => !downloading && handleDownloadInvoices()}
              title="Download selected invoices as PDF"
              disabled={downloading}
            >
              <DownloadIcon fontSize="small" />
              {downloading ? 'Downloading...' : 'Download Selected'}
            </button>
          </div>
        )}
      </div>

      {/* Invoices Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={selectedInvoices.length === filteredInvoices.length && filteredInvoices.length > 0}
                />
              </th>
              <th>Invoice #</th>
              <th>Document Type</th>
              <th>Date</th>
              <th>Client</th>
              <th>Amount</th>
              <th>HSN Items</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedFilteredInvoices.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center">
                  No invoices found
                </td>
              </tr>
            ) : (
              sortedFilteredInvoices.map(invoice => {
                const invoiceId = invoice.invoice_id;
                const isDownloading = downloadingInvoices.includes(invoiceId);

                return (
                  <tr key={invoiceId}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedInvoices.includes(invoiceId)}
                        onChange={() => handleSelectInvoice(invoiceId)}
                      />
                    </td>
                    <td>{invoice.invoice_number}</td>
                    <td>
                      <span className={`document-type-badge ${(invoice.document_type || 'invoice').toLowerCase()}`}>
                        {invoice.document_type === 'debit_note' ? 'Debit Note' :
                          invoice.document_type === 'credit_note' ? 'Credit Note' :
                            invoice.document_type === 'delivery_challan' ? 'Delivery Challan' : 'Invoice'}
                      </span>
                    </td>
                    <td>{formatDate(invoice.invoice_date)}</td>
                    <td>
                      <div className="client-info">
                        <span className="client-name">{invoice.bill_to_name}</span>
                        <span className="client-mobile">{invoice.bill_to_mobile}</span>
                      </div>
                    </td>
                    <td>₹{Number(invoice.total || 0).toFixed(2)}</td>
                    <td>
                      {invoice.items && Array.isArray(invoice.items) ? (
                        <div className="hsn-list">
                          {invoice.items.map((item, idx) =>
                            item.hsn ? <span key={idx} className="hsn-tag">{item.hsn}</span> : null
                          ).filter(Boolean).slice(0, 3)}
                          {invoice.items.filter(item => item.hsn).length > 3 &&
                            <span className="hsn-more">+{invoice.items.filter(item => item.hsn).length - 3} more</span>
                          }
                        </div>
                      ) : 'N/A'}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-icon"
                          title="Preview Invoice"
                          onClick={() => handlePreviewInvoice(invoice)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </button>
                        <button
                          className="btn btn-icon"
                          title="Share"
                          onClick={() => handleShareInvoice(invoice)}
                          style={{ color: '#007bff' }}
                        >
                          <ShareIcon fontSize="small" />
                        </button>
                        <Link
                          to={`/invoices/${invoiceId}/edit`}
                          className="btn btn-icon"
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </Link>
                        <button
                          className="btn btn-icon"
                          onClick={() => handleCancelInvoice(invoice)}
                          title="Cancel Document"
                          style={{ color: '#dc3545' }}
                        >
                          <BlockIcon fontSize="small" />
                        </button>
                        <button
                          className="btn btn-icon"
                          onClick={() => handleDuplicateInvoice(invoice)}
                          title="Duplicate"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </button>
                        <button
                          className={`btn btn-icon ${isDownloading ? 'disabled' : ''}`}
                          onClick={() => !isDownloading && handleDownloadSingleInvoice(invoice)}
                          title="Download as PDF"
                          disabled={isDownloading}
                        >
                          {isDownloading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DownloadIcon fontSize="small" />
                          )}
                        </button>
                        <button
                          className="btn btn-icon"
                          title="Print Options"
                          onClick={() => setPrintOptions({ open: true, invoice })}
                        >
                          <PrintIcon fontSize="small" />
                        </button>
                        <button
                          className="btn btn-icon"
                          onClick={() => handleGenerateEwayBill(invoice)}
                          title="Open GST E-way Bill Portal"
                          style={{ color: '#ff6b35' }}
                        >
                          <EwayBillIcon fontSize="small" />
                        </button>
                        <button
                          className="btn btn-icon text-danger"
                          onClick={() => handleDeleteInvoices([invoiceId])}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <InvoicePreviewModal
        show={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        invoice={viewingInvoice}
        mode={previewMode}
      />

      {/* Print Options Modal */}
      {printOptions.open && (
        <div
          className="modal-overlay"
          onClick={() => setPrintOptions({ open: false, invoice: null })}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 8,
              width: 360,
              maxWidth: '90%',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee' }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>Print Invoice</h3>
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  if (printOptions.invoice) {
                    handlePrintInvoice(printOptions.invoice, 'standard');
                  }
                  setPrintOptions({ open: false, invoice: null });
                }}
              >
                Standard Print
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  if (printOptions.invoice) {
                    handleThermalPrint(printOptions.invoice);
                  }
                  setPrintOptions({ open: false, invoice: null });
                }}
              >
                Thermal Printer
              </button>
            </div>
            <div style={{ padding: '10px 20px', borderTop: '1px solid #eee', textAlign: 'right' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setPrintOptions({ open: false, invoice: null })}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        show={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, invoiceIds: [] })}
        onConfirm={confirmDeleteInvoices}
        title="Delete Invoice(s)?"
        message="Are you sure you want to delete the selected invoice(s)? This action cannot be undone."
        confirmText="Delete"
        confirmColor="btn-danger"
      />
    </main>
  );
};

export default InvoicesPage; 